import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, X, Navigation, Play, Square, Trash2, Pencil } from "lucide-react";
import { TRACKER_COLORS, TRACKER_TYPES, TRACKER_SVG } from "./mapConstants";

// Sortering: Bil → Trækvogn → Person, alfabetisk inden for hver type
const TRACKER_TYPE_ORDER = { vehicle: 0, cart: 1, person: 2 };
function sortTrackers(trackers) {
  return [...(trackers || [])].sort((a, b) => {
    const ao = TRACKER_TYPE_ORDER[a.tracker_type] ?? 99;
    const bo = TRACKER_TYPE_ORDER[b.tracker_type] ?? 99;
    if (ao !== bo) return ao - bo;
    return (a.name || '').localeCompare(b.name || '', 'da', { sensitivity: 'base' });
  });
}

export function GpsTrackerPanel({
  trackers,
  isAdmin,
  user,
  showGPSPanel,
  setShowGPSPanel,
  setShowAddTracker,
  activeGPSTracking,
  startGPSTracking,
  stopGPSTracking,
  deleteGPSTracker,
  editGPSTracker,
  timeSinceUpdate,
}) {
  if (!showGPSPanel) return null;
  const sortedTrackers = sortTrackers(trackers);

  return (
    <div className="fixed top-16 right-4 z-[1000] w-72 bg-zinc-900/95 border border-zinc-700 rounded-lg shadow-xl backdrop-blur-sm max-h-[calc(100vh-120px)] overflow-y-auto" data-testid="gps-tracker-panel">
      <div className="p-3 border-b border-zinc-700 flex items-center justify-between">
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          <Navigation className="h-4 w-4 text-blue-400" />
          GPS Trackere ({sortedTrackers.length})
        </h3>
        <div className="flex items-center gap-1">
          {isAdmin && (
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setShowAddTracker(true)} data-testid="add-tracker-panel-btn">
              <Plus className="h-3 w-3" />
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setShowGPSPanel(false)}>
            <X className="h-3 w-3" />
          </Button>
        </div>
      </div>
      
      {sortedTrackers.length === 0 ? (
        <div className="p-4 text-center text-zinc-400 text-sm">
          <p>Ingen trackere oprettet</p>
          {isAdmin && (
            <Button size="sm" className="mt-2" onClick={() => setShowAddTracker(true)}>
              <Plus className="h-3 w-3 mr-1" /> Opret tracker
            </Button>
          )}
        </div>
      ) : (
        <div className="divide-y divide-zinc-800">
          {sortedTrackers.map(tracker => {
            const typeInfo = TRACKER_TYPES.find(t => t.value === tracker.tracker_type) || TRACKER_TYPES[0];
            const hasPosition = tracker.lat && tracker.lng && tracker.lat !== 0;
            return (
              <div key={tracker.id} className="p-3 hover:bg-zinc-800/50" data-testid={`gps-panel-tracker-${tracker.id}`}>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: tracker.color || '#e11d48' }}>
                    <span dangerouslySetInnerHTML={{ __html: TRACKER_SVG[typeInfo.icon] }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{tracker.name}</p>
                    <p className="text-xs text-zinc-400">
                      {typeInfo.label}
                      {hasPosition ? ` · ${timeSinceUpdate(tracker.last_updated)} siden` : ' · Ingen position endnu'}
                      {(tracker.speed || 0) > 0.5 ? ` · ${((tracker.speed || 0) * 3.6).toFixed(0)} km/t` : ''}
                    </p>
                    {tracker.description && (
                      <p className="text-[10px] text-zinc-500 truncate mt-0.5" title={tracker.description}>{tracker.description}</p>
                    )}
                  </div>
                  <div className={`w-2 h-2 rounded-full shrink-0 ${
                    !hasPosition ? 'bg-zinc-500' :
                    timeSinceUpdate(tracker.last_updated).endsWith('s') ? 'bg-green-400' :
                    timeSinceUpdate(tracker.last_updated).endsWith('m') ? 'bg-yellow-400' : 'bg-red-400'
                  }`} />
                </div>
                <div className="flex gap-1 mt-2">
                  {activeGPSTracking !== tracker.id ? (
                    <button 
                      onClick={() => startGPSTracking(tracker.id)}
                      className="text-xs px-2 py-1 bg-green-600/20 text-green-400 border border-green-600/30 rounded hover:bg-green-600/30 flex items-center gap-1"
                      data-testid={`start-tracking-${tracker.id}`}
                    >
                      <Play className="h-3 w-3" /> Start
                    </button>
                  ) : (
                    <button 
                      onClick={stopGPSTracking}
                      className="text-xs px-2 py-1 bg-red-600/20 text-red-400 border border-red-600/30 rounded hover:bg-red-600/30 flex items-center gap-1"
                    >
                      <Square className="h-3 w-3" /> Stop
                    </button>
                  )}
                  {isAdmin && editGPSTracker && (
                    <button
                      onClick={() => editGPSTracker(tracker)}
                      className="text-xs px-2 py-1 text-blue-400 hover:bg-blue-600/20 rounded"
                      title="Rediger tracker (kun admin)"
                      data-testid={`edit-tracker-${tracker.id}`}
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                  )}
                  {isAdmin && (
                    <button 
                      onClick={() => deleteGPSTracker(tracker.id, tracker.name)}
                      className="text-xs px-2 py-1 text-red-400 hover:bg-red-600/20 rounded"
                      title="Slet tracker (kun admin)"
                      data-testid={`delete-tracker-${tracker.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      <div className="p-2 border-t border-zinc-700 text-center">
        <p className="text-[10px] text-zinc-500">GDPR: Tracking kun aktiv mens brugeren er logget ind</p>
      </div>
    </div>
  );
}

export function GpsTrackerAddDialog({
  showAddTracker,
  setShowAddTracker,
  newTrackerName,
  setNewTrackerName,
  newTrackerType,
  setNewTrackerType,
  selectedTrackerColor,
  setSelectedTrackerColor,
  newTrackerDescription,
  setNewTrackerDescription,
  createGPSTracker,
  // Edit-mode
  isEditing,
  saveTrackerEdits,
}) {
  if (!showAddTracker) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[2000] p-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 w-full max-w-md" data-testid="add-gps-tracker-dialog">
        <h3 className="font-bold text-lg mb-2 text-white flex items-center gap-2">
          <Navigation className="h-5 w-5 text-blue-400" />
          {isEditing ? 'Rediger GPS-tracker' : 'Ny GPS-tracker'}
        </h3>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block text-zinc-300">Navn *</label>
            <Input
              value={newTrackerName}
              onChange={(e) => setNewTrackerName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  isEditing ? saveTrackerEdits && saveTrackerEdits() : createGPSTracker();
                }
              }}
              placeholder="F.eks. Kørsel - Bil 1, Affaldsvogn 2..."
              className="bg-zinc-800 border-zinc-600"
              data-testid="gps-tracker-name-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block text-zinc-300">Type</label>
            <div className="flex gap-2">
              {TRACKER_TYPES.map(tt => (
                <button
                  key={tt.value}
                  onClick={() => setNewTrackerType(tt.value)}
                  className={`flex-1 p-3 rounded-lg border text-center transition-all ${
                    newTrackerType === tt.value 
                      ? 'border-blue-500 bg-blue-500/10 text-white' 
                      : 'border-zinc-700 bg-zinc-800 text-zinc-400 hover:border-zinc-500'
                  }`}
                  data-testid={`tracker-type-${tt.value}`}
                >
                  <div className="w-8 h-8 rounded-full mx-auto mb-1 flex items-center justify-center" style={{ background: selectedTrackerColor }}>
                    <span dangerouslySetInnerHTML={{ __html: TRACKER_SVG[tt.icon] }} />
                  </div>
                  <span className="text-xs font-medium">{tt.label}</span>
                </button>
              ))}
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium mb-2 block text-zinc-300">Vælg farve</label>
            <div className="flex gap-2 flex-wrap">
              {TRACKER_COLORS.map((tc) => (
                <button
                  key={tc.color}
                  onClick={() => setSelectedTrackerColor(tc.color)}
                  className={`w-9 h-9 rounded-full transition-all ${
                    selectedTrackerColor === tc.color 
                      ? 'ring-2 ring-white ring-offset-2 ring-offset-zinc-900 scale-110' 
                      : 'hover:scale-105 opacity-70 hover:opacity-100'
                  }`}
                  style={{ background: tc.color }}
                  title={tc.label}
                  data-testid={`color-${tc.color}`}
                />
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block text-zinc-300">Beskrivelse</label>
            <textarea
              value={newTrackerDescription || ''}
              onChange={(e) => setNewTrackerDescription && setNewTrackerDescription(e.target.value)}
              placeholder="F.eks. 'Kører i nordzonen' eller 'Renovation - team 2'"
              rows={2}
              className="w-full rounded border border-zinc-600 bg-zinc-800 text-sm p-2 text-white placeholder:text-zinc-500"
              data-testid="gps-tracker-description-input"
            />
          </div>

          {!isEditing && (
            <div className="bg-zinc-800/60 rounded-lg p-3 border border-zinc-700">
              <p className="text-xs font-bold text-zinc-300 mb-1">Hvordan bruger du trackeren?</p>
              <ol className="text-xs text-zinc-400 space-y-1 list-decimal ml-3">
                <li>Opret trackeren her med et navn og type</li>
                <li>Åbn appen på en telefon/tablet i bilen/vognen</li>
                <li>Tryk <span className="text-green-400">Start</span> på den rigtige tracker i GPS-panelet</li>
                <li>Positionen sendes automatisk hvert 30. sekund</li>
              </ol>
              <p className="text-[10px] text-zinc-500 mt-2">
                GDPR: Tracking er kun aktiv mens brugeren er logget ind. Ved logout stoppes tracking automatisk.
              </p>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => { setShowAddTracker(false); setNewTrackerName(''); setNewTrackerDescription && setNewTrackerDescription(''); }}>
            Annuller
          </Button>
          {isEditing ? (
            <Button onClick={saveTrackerEdits} disabled={!newTrackerName.trim()} data-testid="save-gps-tracker-btn">
              Gem ændringer
            </Button>
          ) : (
            <Button onClick={createGPSTracker} disabled={!newTrackerName.trim()} data-testid="create-gps-tracker-btn">
              Opret tracker
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
